import { CapsicumError, useLogger, usePrevious, useSubscribe } from '@capsicum/common';
import { usePathname } from 'next/navigation';
import * as React from 'react';
import { type PropsWithChildren } from 'react';

import { GenericError } from '../../ui/pages/errors/GenericError';
import { GenericErrorWrapper } from '../../ui/pages/errors/GenericErrorWrapper';

const ErrorServiceBoundary = ({ children }: PropsWithChildren) => {
	const [errorToDisplay, setErrorToDisplay] = React.useState<Error | undefined>(undefined);
	const [errorId, setErrorId] = React.useState<string | undefined>(undefined);
	const resetAfterNav = React.useRef(false);
	const path = usePathname();
	const prePath = usePrevious(path);
	const logger = useLogger();

	React.useEffect(() => {
		if (prePath && prePath !== path && resetAfterNav.current) {
			resetAfterNav.current = false;
			setErrorToDisplay(undefined);
		}
	}, [path, prePath, setErrorToDisplay]);

	const resetError = (waitNav = false) => {
		if (waitNav) {
			resetAfterNav.current = true;
		} else {
			setErrorToDisplay(undefined);
		}
	};

	useSubscribe<Error>('globalError', error => {
		setErrorToDisplay(error);
		const errorIds = logger.error(error);
		const id = (errorIds?.length ?? 0) > 0 ? errorIds?.[0] : undefined;

		setErrorId(id);
	});

	useSubscribe('resetGlobalError', () => {
		resetError();
	});

	return errorToDisplay ? (
		<GenericErrorWrapper>
			<RenderError error={errorToDisplay} errorId={errorId} reset={resetError} />
		</GenericErrorWrapper>
	) : (
		children
	);
};

export { ErrorServiceBoundary };

const RenderError = ({ error, errorId, reset }: { error: Error; errorId?: string; reset: () => void }) => {
	if (error instanceof CapsicumError && error.component !== undefined) {
		return <error.component reset={reset} />;
	}

	return <GenericError error={error} errorId={errorId} reset={reset} />;
};
